import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './Events.css';
import GeographicalMap, { MapPlace, MapState } from '../components/map/GeographicalMap';
import LocationInfo from '../components/map/utils/LocationInfo';
import RadiusControl from '../components/map/utils/RadiusControl';
import GeolocationWarning from '../components/map/utils/GeolocationWarning';
import Calendar, { CalendarEvent } from '../components/calendar/Calendar';
import { fetchEvents, Event } from '../services/eventService';
import { fetchBadges, Badge } from '../services/badgeService';
import { getPageTitle } from '../constants';

const EventPage: React.FC = () => {
  const [mapState, setMapState] = useState<MapState | null>(null);

  const [selectedEventType, setSelectedEventType] = useState('');
  const [selectedSportType, setSelectedSportType] = useState('');
  const [events, setEvents] = useState<Event[]>([]);
  const [badges, setBadges] = useState<Badge[]>([]);
  const [calendarRange, setCalendarRange] = useState<{ start: Date; end: Date } | null>(null);

  useEffect(() => {
    document.title = getPageTitle('Events');
  }, []);

  useEffect(() => {
    const loadBadges = async () => {
      try {
        const fetchedBadges = await fetchBadges();
        setBadges(fetchedBadges);
      } catch (error) {
        console.error('Failed to fetch badges:', error);
      }
    };

    loadBadges();
  }, []);

  useEffect(() => {
    if (!calendarRange) return;

    let ignore = false;

    const loadEvents = async () => {
      try {
        const fetchedEvents = await fetchEvents({
          eventType: selectedEventType,
          sportType: selectedSportType,
          startDate: calendarRange.start,
          endDate: calendarRange.end,
        });
        if (!ignore) {
          setEvents(fetchedEvents);
        }
      } catch (error) {
        console.error('Failed to fetch events:', error);
      }
    };

    loadEvents();

    return () => {
      ignore = true;
    };
  }, [selectedEventType, selectedSportType, calendarRange]);

  const handleCalendarRangeChange = useCallback((start: Date, end: Date) => {
    setCalendarRange((prev) => {
      if (prev && prev.start.getTime() === start.getTime() && prev.end.getTime() === end.getTime()) {
        return prev;
      }
      return { start, end };
    });
  }, []);

  const handleEventClick = (event: MapPlace) => {
    console.log('Event clicked:', event);
  };

  const eventMarkers: MapPlace[] = events.map(event => ({
    id: event.id,
    name: event.name,
    address: event.location,
    description: event.description,
    sportType: event.sportType,
    placeType: event.eventType,
    lat: event.lat,
    lng: event.lng,
  }));

  const calendarEvents: CalendarEvent[] = useMemo(
    () =>
      events
        .filter((e) => !!e.startDate)
        .map((e) => ({
          id: e.id,
          title: e.name,
          start: new Date(e.startDate),
          end: e.endDate ? new Date(e.endDate) : undefined,
          location: e.location,
          type: e.eventType,
          badges: e.sportType ? [e.sportType] : [],
          organizer: e.organizer,
        })),
    [events]
  );

  const handleCalendarEventClick = (ev: CalendarEvent) => {
    console.log('Calendar event clicked:', ev);
  };

  return (
    <>
      <div className="page-content">
        <div className="search-panel">
          {mapState && (
            <>
              <LocationInfo
                userLocation={mapState.userLocation}
                locationAddress={mapState.locationAddress}
              />
              <RadiusControl
                searchRadius={mapState.searchRadius}
                onRadiusChange={mapState.onRadiusChange}
              />
            </>
          )}

          <div className="search-form">
            <div className="form-group">
              <label htmlFor="eventType">Event Type</label>
              <select
                id="eventType"
                value={selectedEventType}
                onChange={(e) => setSelectedEventType(e.target.value)}
                className="form-select"
              >
                <option value="">All Event Types</option>
                <option value="tournament">Tournament</option>
                <option value="match">Match</option>
                <option value="training">Training Session</option>
                <option value="workshop">Workshop</option>
                <option value="competition">Competition</option>
                <option value="meetup">Meetup</option>
                <option value="league">League</option>
                <option value="camp">Camp</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="sportType">Sport Type (Badge) - {badges.length} loaded</label>
              <select
                id="sportType"
                value={selectedSportType}
                onChange={(e) => setSelectedSportType(e.target.value)}
                className="form-select"
              >
                <option value="">All Sports</option>
                {badges.map((badge) => (
                  <option key={badge.id} value={badge.id.toString()}>
                    {badge.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="filters">
            <h3>Filters</h3>
            <div className="filter-group">
              <label>
                <input type="checkbox" /> Free Events
              </label>
              <label>
                <input type="checkbox" /> Open Registration
              </label>
              <label>
                <input type="checkbox" /> Beginner Friendly
              </label>
            </div>
          </div>
        </div>

        <div className="map-area">
          {mapState && (
            <GeolocationWarning geolocationAllowed={mapState.geolocationAllowed} />
          )}
          <GeographicalMap
            places={eventMarkers}
            onPlaceClick={handleEventClick}
            getPlaceDetailUrl={(p) => `/event/${p.id}`}
            onMapStateChange={setMapState}
          />
        </div>
      </div>

      <div className="results-section">
        <h2>Events</h2>
        <Calendar
          events={calendarEvents}
          onEventClick={handleCalendarEventClick}
          onRangeChange={handleCalendarRangeChange}
        />
      </div>
    </>
  );
};

export default EventPage;
